import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from "@tanstack/react-query";
import { createContext, useContext } from "react";

import api, { ApiCatalog } from "@/api";
import { PARNASSUS_HOST } from "@/challenges/utils/constants";

import type {
  ChallengeAPIResponse,
  ChallengeArticle,
} from "@/challenges/utils/interfaces";

interface ChallengeArticleProviderProps {
  children: React.ReactNode;
  contentId: string;
}

type ChallengeArticleResponse = ChallengeAPIResponse<ChallengeArticle>;

interface ChallengeArticleContextValue {
  articleData: ChallengeArticleResponse | null;
  refetch:
    | ((
        options?: RefetchOptions | undefined,
      ) => Promise<QueryObserverResult<ChallengeArticleResponse, Error>>)
    | null;
}

export const ChallengeArticleContext =
  createContext<ChallengeArticleContextValue>({
    articleData: null,
    refetch: null,
  });

export const ChallengeArticleProvider = ({
  children,
  contentId,
}: ChallengeArticleProviderProps) => {
  const { data: articleData = null, refetch } =
    useQuery<ChallengeArticleResponse>({
      queryKey: [ApiCatalog.challengeArticle.key, contentId],
      queryFn: async () =>
        await api({ basePath: PARNASSUS_HOST }).get(
          ApiCatalog.challengeArticle.endpoint.replace(
            "[contentId]",
            contentId,
          ),
        ),
    });

  return (
    <ChallengeArticleContext.Provider value={{ articleData, refetch }}>
      {children}
    </ChallengeArticleContext.Provider>
  );
};

const useChallengeArticleContext = () => {
  const context = useContext(ChallengeArticleContext);

  if (!context) {
    throw new Error(
      "useChallengeArticleContext must be used within an ChallengeArticleContext.",
    );
  }

  return context;
};

export default useChallengeArticleContext;
